"use client";

import { useEffect, useState } from "react";
import { useCartLines } from "@/hooks/useCart";
import {
  flagMixedPreorderLines,
  validateCartLine,
  type ProductFetchResult,
  type ValidatedCartLine,
} from "@/lib/cart-validation";
import type { ApiResponse, ProductWithDiscount } from "@/types";

// "찾을 수 없음"과 "확인하지 못함"을 서로 다른 상태로 돌려준다(왜 이 구분이
// 필요한지는 cart-validation.ts의 ProductFetchResult 주석 참고). /api/products/
// [id]는 상품이 실제로 없으면 명확히 404를 응답하므로(src/app/api/products/
// [id]/route.ts), 그 경우만 not_found로 본다. 네트워크 자체가 실패했거나
// (fetch가 reject) 5xx·잘못된 응답이면 unverified로 남겨, 다시 시도할 수 있게 한다.
async function fetchProduct(productId: string): Promise<ProductFetchResult> {
  const response = await fetch(`/api/products/${productId}`).catch(() => null);
  if (!response) {
    return { status: "unverified", product: null };
  }
  if (response.status === 404) {
    return { status: "not_found", product: null };
  }
  if (!response.ok) {
    return { status: "unverified", product: null };
  }
  const json = (await response
    .json()
    .catch(() => null)) as ApiResponse<ProductWithDiscount> | null;
  if (!json?.success || !json.data) {
    return { status: "unverified", product: null };
  }
  return { status: "found", product: json.data };
}

interface UseValidatedCartResult {
  // 담긴 상품이 있는데 아직 서버 데이터를 못 받아온 상태(최초 로드,
  // reload() 직후). 빈 장바구니는 loading이 즉시 false로 끝난다.
  loading: boolean;
  lines: ValidatedCartLine[];
  reload: () => void;
}

// 장바구니에 담긴 상품 id마다 /api/products/[id]를 호출해 최신 가격·재고·
// 판매 상태를 받아오고, cart-validation.ts로 각 줄을 검증한다. 장바구니
// 페이지와 주문서(카트 모드)가 이 훅을 공유해 같은 데이터로 같은 판단을
// 내리게 한다.
export function useValidatedCart(): UseValidatedCartResult {
  const cartLines = useCartLines();
  // 항상 true로 시작해 아래 effect가 정리한다. cartLines.length로 초기값을
  // 정하면, useSyncExternalStore가 하이드레이션 직후 서버 스냅샷([])에서
  // 실제 로컬스토리지 값으로 넘어가는 첫 렌더 사이에 이 초기값이 갱신되지
  // 않아(useState 초기값은 최초 1회만 평가됨) 실제로는 담긴 상품이 있는데도
  // loading이 false로 굳는 경우가 생긴다.
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<Map<string, ProductFetchResult>>(
    new Map(),
  );
  const [reloadToken, setReloadToken] = useState(0);

  // productId 목록이 실제로 바뀔 때만 다시 불러오도록 문자열로 묶어 의존성에
  // 넣는다(배열 참조는 매 렌더 새로 만들어져 useEffect가 매번 도는 것을 막는다).
  const productIdsKey = [...new Set(cartLines.map((line) => line.productId))]
    .sort()
    .join(",");

  useEffect(() => {
    const productIds = productIdsKey ? productIdsKey.split(",") : [];
    let cancelled = false;
    // setState 호출을 전부 이 안(비동기 함수)으로 모아둔다. effect 본문에
    // 직접 동기적으로 setState를 두면(react-hooks/set-state-in-effect) 연쇄
    // 렌더링을 유발할 수 있다는 경고가 뜬다.
    (async () => {
      setLoading(true);
      if (productIds.length === 0) {
        if (!cancelled) {
          setProducts(new Map());
          setLoading(false);
        }
        return;
      }
      const entries = await Promise.all(
        productIds.map(
          async (productId) =>
            [productId, await fetchProduct(productId)] as const,
        ),
      );
      if (cancelled) return;
      setProducts(new Map(entries));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [productIdsKey, reloadToken]);

  // 아직 이 productId의 조회 결과가 map에 없으면(로딩 중) not_found가 아니라
  // unverified로 취급한다 — "확인 전"과 "없다고 확인됨"은 다르다.
  const lines = flagMixedPreorderLines(
    cartLines.map((cartLine) =>
      validateCartLine(
        cartLine,
        products.get(cartLine.productId) ?? {
          status: "unverified",
          product: null,
        },
      ),
    ),
  );

  return { loading, lines, reload: () => setReloadToken((t) => t + 1) };
}
